<template>
  <section
      id="milestone-section"
      class="milestone-section flex flex-col items-center py-20 px-6 w-full bg-blue-300"
  >
    <!-- Header -->
    <div class="responsive-body-large font-semibold text-center text-black mb-4">
      {{ $t('aboutMilestone.embarking.part1') }} <br />
      {{ $t('aboutMilestone.embarking.part2') }}
    </div>

    <div class="mt-11 responsive-title-1 text-center text-black w-[839px] max-md:mt-10 max-md:max-w-full">
      {{ $t('aboutMilestone.loyalty') }}
    </div>
    <!-- Image Section that changes based on current milestone -->
    <div v-touch:swipe="handleSwipe" class="relative flex justify-center mt-12 mb-2 sm:mb-12 overflow-hidden w-full h-[400px]">
      <transition name="fade-slide" mode="out-in">
        <img 
            :key="currentIndex" 
            :src="milestones[currentIndex].images[0].src"
            alt="Milestone Image"
            class="w-96 h-96 rounded-lg object-contain"
        />
      </transition>
    </div>

    <!-- Milestone Date -->
    <div class="responsive-title-3 font-semibold text-center text-black mt-8">
      {{ milestones[currentIndex].date }}
    </div>

    <!-- Timeline and Information Section -->
    <div class="relative flex items-center justify-center h-8 w-full max-w-[1200px] mt-8">
      <!-- Static timeline -->
      <div class="timeline-container flex items-center w-full">
        <div
            v-for="(milestone, index) in milestones"
            :key="index"
            class="flex items-center justify-center w-56 sm:w-74"
        >
          <!-- Dot for each milestone-->
          <div v-if="index === currentIndex" class="absolute z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center mx-3">
            <div class="w-3 h-3 bg-blue-300 rounded-full"></div>
          </div>
          <div v-else
              class="dot rounded-full transition-colors duration-300"
              :class="{
                'bg-black w-2 h-1': index < currentIndex,
                'bg-gray-400 w-2 h-1': index > currentIndex
              }"
              :ref="el => dotRefs[index] = el"
          ></div>
          <div 
              class="timeline-line w-full h-px ml-1 mr-1"
              :class="{
                'bg-black': index < currentIndex,
                'bg-gray-400': index >= currentIndex
              }"></div>
        </div>
      </div>
    </div>

    <!-- Milestone Description -->
    <transition name="fade-slide" mode="out-in">
      <div
          :key="currentIndex"
          class="mt-9 responsive-body-large text-center w-[428px] max-md:max-w-full"
      >
        {{ milestones[currentIndex].description }}
      </div>
    </transition>

    <!-- Navigation Buttons -->
    <div class="flex gap-5 mt-10">
      <button
          @click="prevMilestone"
          :disabled="currentIndex === 0"
          class="p-3 rounded-full border !border-black disabled:opacity-50"
      >
        <img src="https://cdn.builder.io/api/v1/image/assets/TEMP/02dec2e545db09402ccb0d8312adfa876efae3629ed4db06b2fa47dcc7be040e?apiKey=076028e7c6ce40f6853686e5fbd07612&"
             class="w-4 aspect-square" />
      </button>
      <button
          @click="nextMilestone"
          :disabled="currentIndex === milestones.length - 1"
          class="p-3 rounded-full border !border-black disabled:opacity-50"
      >
        <img src="https://cdn.builder.io/api/v1/image/assets/TEMP/2cd1b83b6bb5a2911452e135c8e6e176a582a765de4c3fb4ed58200e06d5ef82?apiKey=076028e7c6ce40f6853686e5fbd07612&"
             class="w-4 aspect-square" />
      </button>
    </div>
    
  </section>
</template>

<script setup>
import {ref, watch, nextTick, onMounted, onBeforeUnmount} from 'vue';
import { useI18n } from 'vue-i18n';
import gsap from 'gsap';
import { getAsset } from "@/helpers/assetsUtils";

const { t } = useI18n()

// Data
const currentIndex = ref(0);
const dotRefs = ref([]);
const milestones = ref([
  {
    date: t('aboutMilestone.items[0].date'),
    description: t('aboutMilestone.items[0].description'),
    images: [{ src: getAsset('milestones','1987.png') }]
  },
  {
    date: t('aboutMilestone.items[1].date'),
    description: t('aboutMilestone.items[1].description'),
    images: [{ src: getAsset('milestones','1988.jpg') }]
  },
  {
    date: t('aboutMilestone.items[2].date'),
    description: t('aboutMilestone.items[2].description'),
    images: [{ src: getAsset('milestones','1991.png') }]
  },
  {
    date: t('aboutMilestone.items[3].date'),
    description: t('aboutMilestone.items[3].description'),
    images: [{ src: getAsset('milestones','1991_london.png') }]
  },
  {
    date: t('aboutMilestone.items[4].date'),
    description: t('aboutMilestone.items[4].description'),
    images: [{ src: getAsset('milestones','1992.png') }]
  },
  {
    date: t('aboutMilestone.items[5].date'),
      description: t('aboutMilestone.items[5].description'),
      images: [
        { src: getAsset('milestones','1994.png') }
      ]
  },
  {
    date: t('aboutMilestone.items[6].date'),
    description: t('aboutMilestone.items[6].description'),
    images: [
      { src: getAsset('milestones','1995.png') },
    ]
  },
  {
    date: t('aboutMilestone.items[7].date'),
    description: t('aboutMilestone.items[7].description'),
    images: [
      { src: getAsset('milestones','1995_fountaine_pajot.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[8].date'),
    description: t('aboutMilestone.items[8].description'),
    images: [
      { src: getAsset('milestones','1995_nomicos.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[9].date'),
    description: t('aboutMilestone.items[9].description'),
    images: [
      { src: getAsset('milestones','1996.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[10].date'),
    description: t('aboutMilestone.items[10].description'),
    images: [
      { src: getAsset('milestones','1997.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[11].date'),
    description: t('aboutMilestone.items[11].description'),
    images: [
      { src: getAsset('milestones','1998.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[12].date'),
    description: t('aboutMilestone.items[12].description'),
    images: [
      { src: getAsset('milestones','1999.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[13].date'),
    description: t('aboutMilestone.items[13].description'),
    images: [
      { src: getAsset('milestones','2000.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[14].date'),
    description: t('aboutMilestone.items[14].description'),
    images: [
      { src: getAsset('milestones','2001.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[15].date'),
    description: t('aboutMilestone.items[15].description'),
    images: [
      { src: getAsset('milestones','2002.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[16].date'),
    description: t('aboutMilestone.items[16].description'),
    images: [
      { src: getAsset('milestones','2006.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[17].date'),
    description: t('aboutMilestone.items[17].description'),
    images: [
      { src: getAsset('milestones','2008.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[18].date'),
    description: t('aboutMilestone.items[18].description'),
    images: [
      { src: getAsset('milestones','2009.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[19].date'),
    description: t('aboutMilestone.items[19].description'),
    images: [
      { src: getAsset('milestones','2010.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[20].date'),
    description: t('aboutMilestone.items[20].description'),
    images: [
      { src: getAsset('milestones','2011.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[21].date'),
    description: t('aboutMilestone.items[21].description'),
    images: [
      { src: getAsset('milestones','2012_2013.png') }
    ]
  },
  {
    date: t('aboutMilestone.items[22].date'),
    description: t('aboutMilestone.items[22].description'),
    images: [
      { src: getAsset('milestones','2012.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[23].date'),
    description: t('aboutMilestone.items[23].description'),
    images: [
      { src: getAsset('milestones','2013.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[24].date'),
    description: t('aboutMilestone.items[24].description'),
    images: [
      { src: getAsset('milestones','2014.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[25].date'),
    description: t('aboutMilestone.items[25].description'),
    images: [
      { src: getAsset('milestones','2015.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[26].date'),
    description: t('aboutMilestone.items[26].description'),
    images: [
      { src: getAsset('milestones','2016.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[27].date'),
    description: t('aboutMilestone.items[27].description'),
    images: [
      { src: getAsset('milestones','2017.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[28].date'),
    description: t('aboutMilestone.items[28].description'),
    images: [
      { src: getAsset('milestones','2018.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[29].date'),
    description: t('aboutMilestone.items[29].description'),
    images: [
      { src: getAsset('milestones','2019.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[30].date'),
    description: t('aboutMilestone.items[30].description'),
    images: [
      { src: getAsset('milestones','2020.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[31].date'),
    description: t('aboutMilestone.items[31].description'),
    images: [
      { src: getAsset('milestones','2021.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[32].date'),
    description: t('aboutMilestone.items[32].description'),
    images: [
      { src: getAsset('milestones','2022.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[33].date'),
    description: t('aboutMilestone.items[33].description'),
    images: [
      { src: getAsset('milestones','2023.jpg') }
    ]
  },
  {
    date: t('aboutMilestone.items[34].date'),
    description: t('aboutMilestone.items[34].description'),
    images: [
      { src: getAsset('milestones','2024.png') }
    ]
  }
])


const nextMilestone = () => {
  if (currentIndex.value < milestones.value.length - 1) {
    currentIndex.value += 1;
  }
};

const prevMilestone = () => {
  if (currentIndex.value > 0) {
    currentIndex.value -= 1;
  }
};

const handleSwipe = (direction) => {
  if (direction === 'left') {
    nextMilestone();
  }
  else if (direction === 'right') {
    prevMilestone();
  }
};

// Dot animation
const animateActiveDot = (index) => {
  const dot = dotRefs.value[index]
  if (!dot) return
  gsap.fromTo(dot, { scale: 1 }, { scale: 1.5, duration: 0.4, ease: 'back.out(2)' })
}

// Watch for milestone changes 
watch(currentIndex, async (newIndex) => {
  await nextTick();
  animateActiveDot(newIndex);
})


const autoPlayInterval = ref(null) // store interval ID

const startAutoPlay = () => {
  autoPlayInterval.value = setInterval(() => {
    if (currentIndex.value < milestones.value.length - 1) {
      currentIndex.value++
    } else {
      currentIndex.value = 0 // loop back to first milestone
    }
  }, 4000) // change milestone for every 4 seconds
}

const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

onMounted(() => {
  animateActiveDot(currentIndex.value) // animate first dot 
  startAutoPlay()
})

// Stop auto-play when component is unmounted
onBeforeUnmount(() => stopAutoPlay())


</script>

<style scoped>

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
